/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import Table from "components/Table";
import { playerCardsListColumn } from "utils/tableHelper/playerCardTableHelper";
import ContentShell from "components/ContentShell";
import {
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  DialogActions,
  Typography,
  Chip,
  DialogContent,
  DialogTitle,
  Dialog,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import configData from "config/config.json";
import { getManualPlayerCards } from "services/playerCardService";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import { generateOptions, opacity2hex, sortArray } from "utils/miscUtils";
import SearchInput from "components/SearchInput";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import ViewFullCardModal from "screens/modals/ViewFullCardModal";
import { defaultPageNo } from "utils/constants";
import { SimpleSelectInput } from "components/FormSelectInput";
import useSearchHook from "hooks/useSearch";
import CircularProgress from "@mui/material/CircularProgress";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "fit-content",
    flexFlow: "row wrap",
    gap: 4,
  },
  field: {
    width: 180,
  },
  buttonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 24,
  },
  button: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    textTransform: "none",
    height: 30,
    padding: "0 16px",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
    },
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  fileInput: {
    display: "none",
  },
  dialogContent: {
    maxHeight: "60vh",
    overflow: "auto",
  },
  errorItem: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  successChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
  },
  errorChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
  },
}));

const RowActions = ({ data, actionHandlers: { handleDisable } }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRowAction = (data: any) => {
    setAnchorEl(null);
    navigate(
      configData.routes.playerDetails.editPlayerCard?.replace(":id", data?._id)
    );
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleRowAction(data)}>Edit</MenuItem>
        <MenuItem onClick={() => handleDisable(data?._id)}>
          {data?.isDisabled ? "Enable" : "Disable"}
        </MenuItem>
      </Menu>
    </>
  );
};

const FilterSection = ({
  handleChange = () => {},
  onSubmitClick = () => {},
  filterOptions,
  showSnackbar = () => {},
  onUploadClick = () => {},
  loadingStates = {} as { export: boolean; upload: boolean },
  setLoadingStates = () => {},
}: {
  handleChange?: (e: any) => void;
  onSubmitClick?: () => void;
  filterOptions: any;
  showSnackbar?: (message: string, severity?: string) => void;
  onUploadClick?: () => void;
  loadingStates?: { export: boolean; upload: boolean };
  setLoadingStates?: React.Dispatch<React.SetStateAction<{ export: boolean; upload: boolean }>>;
}) => {
  const classes = useStyles();
  const [uploadResults, setUploadResults] = useState(null);
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const clubOptions = generateOptions({
    items: sessionHelper.clubs,
    id: "externalClubId",
    label: "name",
    addAllOption: true,
  });

  const handleExport = () => {
    setLoadingStates((prev) => ({ ...prev, export: true }));
    showSnackbar("Export PBF Players Started!", "info");
    fetch(
      `${import.meta.env.VITE_CMS_API_URL}/player/manual/pbf/export?token=${sessionHelper?.token}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ pageNo: 1, limit: 1, skipPagination: true }),
      }
    )
      .then((response) => {
        const contentDisposition = response.headers.get("Content-Disposition");
        const fileName = contentDisposition
          ?.split("filename=")[1]
          ?.replace(/["';]/g, "")
          .trim();

        return response.blob().then((blob) => ({ blob, fileName }));
      })
      .then(({ blob, fileName }) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error("Download failed:", err);
        showSnackbar("Failed to export players", "error");
      })
      .finally(() => {
        setLoadingStates((prev) => ({ ...prev, export: false }));
        showSnackbar("Successfully exported PBF Players.", "success");
      });
  };

  const handleFileChange = (event: any) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (
      !file.name.endsWith(".xlsx") &&
      !file.name.endsWith(".xls") &&
      !file.name.endsWith(".csv")
    ) {
      showSnackbar("Incorrect Format. Please upload an Excel file.", "error");
      return;
    }

    setSelectedFile(file);
    setIsConfirmOpen(true);
    if (event?.target) {
      event.target.value = ""; // Reset file input
    }
  };

  const handleConfirmUpload = async () => {
    setIsConfirmOpen(false);
    if (!selectedFile) return;

    setLoadingStates((prev) => ({ ...prev, upload: true }));
    showSnackbar("PBF Upload and Update All Players Started!", "info");

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_CMS_API_URL}/player/manual/pbf/upload?token=${sessionHelper?.token}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || response.statusText);
      }

      const result = await response.json();
      setUploadResults(result.data);
      setIsResultsOpen(true);
      if (result.data.failed) {
        showSnackbar(
          "Oops! Your file has some issues. Please check for missing or incorrect data and try again.",
          "error"
        );
      } else {
        showSnackbar(
          result.message || "Players updated successfully!",
          "success"
        );
      }
      onUploadClick(); // Refresh the list
    } catch (error) {
      showSnackbar(
        error instanceof Error ? error.message : "Failed to update players",
        "error"
      );
    } finally {
      setSelectedFile(null);
      setLoadingStates((prev) => ({ ...prev, upload: false }));
    }
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.field}>
        <SimpleSelectInput
          name="club"
          label="Club"
          options={clubOptions}
          value={filterOptions.club}
          onChange={handleChange}
          customStyle={{ height: 30 }}
        />
      </Box>

      <Box className={classes.field}>
        <SimpleSelectInput
          name="nationality"
          label="Nationality"
          options={sessionHelper.countryOptions}
          value={filterOptions.nationality}
          onChange={handleChange}
          customStyle={{ height: 30 }}
        />
      </Box>

      <Box className={classes.buttonContainer}>
        <Button className={classes.button} onClick={onSubmitClick}>
          Submit
        </Button>
      </Box>

      <Box className={classes.buttonContainer}>
        <Button
          className={classes.button}
          onClick={handleExport}
          disabled={loadingStates.export}
        >
          {loadingStates.export ? (
            <CircularProgress size={20} sx={{ color: "white" }} />
          ) : (
            "Export All Players"
          )}
        </Button>
      </Box>

      <Box className={classes.buttonContainer}>
        <input
          accept=".xlsx,.xls,.csv"
          className={classes.fileInput}
          id="upload-players-file"
          type="file"
          onChange={handleFileChange}
          disabled={loadingStates.upload}
        />
        <label htmlFor="upload-players-file">
          <Button
            component="span"
            className={classes.button}
            disabled={loadingStates.upload}
          >
            {loadingStates.upload ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Upload and Update All Players"
            )}
          </Button>
        </label>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>Confirm Upload</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to upload {selectedFile?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpload}
            color="primary"
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Results Dialog */}
      <Dialog
        open={isResultsOpen}
        onClose={() => setIsResultsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload Results</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box display="flex" alignItems="center" mb={2}>
            <Chip
              label={`Success: ${uploadResults?.success}`}
              className={classes.successChip}
              style={{ marginRight: 8 }}
            />
            <Chip
              label={`Failed: ${uploadResults?.failed}`}
              className={classes.errorChip}
            />
          </Box>

          {uploadResults?.errors?.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Error Details:
              </Typography>
              {uploadResults.errors.map((error, index) => (
                <Box key={index} className={classes.errorItem}>
                  <Typography variant="body2">
                    <strong>Player ID:</strong> {error.playerId}
                  </Typography>
                  <Typography variant="body2" color="error">
                    <strong>Error:</strong> {error.error}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsResultsOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const PBFPlayers = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const [orderDirection, setOrderDirection] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const [filterOptions, setFilterOptions] = useState({
    club: "all",
    nationality: "all",
  });
  const [modalInfo, setModalInfo] = React.useState({
    modalState: false,
    label: "Full Player Card",
    imageUrl: "",
    cardDetails: null,
  });
  const [loadingStates, setLoadingStates] = useState<{
    export: boolean;
    upload: boolean;
  }>({
    export: false,
    upload: false,
  });

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.PLAYER.CARD.UPDATE_ROLES
  );

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: filteredList?.length });

  const handleFileUpload = async (file) => {
    if (!file) return;

    try {
      setLoadingStates((prev) => ({ ...prev, upload: true }));
      setLoading(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${import.meta.env.VITE_CMS_API_URL}/player/manual/pbf/upload?token=${sessionHelper?.token}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || response.statusText);
      }

      const result = await response.json();
      showSnackbar(
        result.message || "Players updated successfully!",
        "success"
      );
      fetchPlayerCards(); // Refresh the list
    } catch (error) {
      showSnackbar(
        error instanceof Error ? error.message : "Failed to update players",
        "error"
      );
    } finally {
      setLoading(false);
      setLoadingStates((prev) => ({ ...prev, upload: false }));
    }
  };

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const openModal = (cardDetails: any) => {
    setTimeout(
      () =>
        setModalInfo((st) => ({
          ...st,
          modalState: true,
          cardDetails: {
            ...cardDetails,
            ...cardDetails?.extraData,
            position: cardDetails?.preferredPosition,
            cardType: cardDetails?.rarity,
            defenceRating: cardDetails?.defenseRating,
          },
        })),
      0
    );
  };

  const closeModal = () => setModalInfo((st) => ({ ...st, modalState: false }));

  const fetchPlayerCards = async () => {
    setLoading(true);
    try {
      let payload = {
        pageNo,
        pageSize,
        club: filterOptions.club,
        nationality: filterOptions.nationality,
      };
      if (searchValue) payload["search"] = searchValue;
      const response = await getManualPlayerCards(payload);
      setFilteredList(
        response?.items?.map((el) => ({ ...el, thumbnail: el?.miniImageUrl }))
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error?.message ?? error);
    }
  };

  const handleFilterChange = (e: any) => {
    const { name, value } = e?.target;
    setFilterOptions((_state) => ({ ..._state, [name]: value }));
  };

  const handleDisable = async (_id) => {
    try {
      setLoading(true);
      showSnackbar("This feature is not implemented");
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message);
    }
  };

  const onSearch = (value: any) => {
    // reset filters if any
    _onSearch(value);
    onChangePage(defaultPageNo);
  };

  useEffect(() => {
    const initialColumns = playerCardsListColumn({
      showActions: false,
      showPreview: false,
    }).filter((col) => col.id !== "actions" || hasEditPermission);
    setColumns(initialColumns);

    fetchPlayerCards();
  }, [pageNo, pageSize, hasEditPermission, searchValue]);

  return (
    <ContentShell
      title="PBF Players"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a player card"
            onSearch={onSearch}
          />
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <FilterSection
          filterOptions={filterOptions}
          handleChange={handleFilterChange}
          onSubmitClick={fetchPlayerCards}
          onUploadClick={handleFileUpload as any}
          showSnackbar={showSnackbar}
          loadingStates={loadingStates}
          setLoadingStates={setLoadingStates}
        />
        <Table
          columns={columns}
          data={filteredList}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={filteredList.length}
          highlightAltRow={true}
          actions={RowActions}
          onColumnClick={onColumnClick}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          actionHandlers={{
            handleDisable: handleDisable,
            openModal: openModal,
          }}
        />
        <ViewFullCardModal
          label={modalInfo.label}
          isOpen={modalInfo.modalState}
          close={closeModal}
          cardDetails={modalInfo.cardDetails}
        />
      </Box>
    </ContentShell>
  );
};

export default PBFPlayers;
